import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { track, setUserId, EVENTS } from "@/lib/analytics";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Activity, Brain, ArrowRight, MapPin, Building2,
  Mail, User, Briefcase, CheckCircle2, AlertTriangle, Calendar, X, Phone,
  Wifi, Zap, ServerCrash, Globe2, Cpu, Bot,
} from "lucide-react";

// ---------- Domain validation (shared by both modals) ----------
const BLOCKED_DOMAINS = [
  "gmail.com", "googlemail.com", "outlook.com", "outlook.com.br", "hotmail.com", "hotmail.com.br",
  "live.com", "msn.com", "yahoo.com", "yahoo.com.br", "ymail.com", "icloud.com", "me.com",
  "aol.com", "protonmail.com", "proton.me", "mail.com", "gmx.com", "zoho.com",
  "uol.com.br", "bol.com.br", "ig.com.br", "terra.com.br", "globo.com", "r7.com",
];
const ADMIN_BYPASS_EMAIL = "thenio@gmail.com";

function isCorporateEmail(email) {
  const e = (email || "").trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return false;
  if (e === ADMIN_BYPASS_EMAIL) return true;
  const domain = e.split("@")[1];
  return !BLOCKED_DOMAINS.includes(domain);
}

// ---------- Landing Page ----------
export default function Landing() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [accessOpen, setAccessOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  // If already authenticated → bounce straight to the cockpit
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Route-guard handshake: any protected route redirects to /?showAccess=1 so the modal pops automatically
  useEffect(() => {
    if (params.get("showAccess") === "1") {
      setAccessOpen(true);
      const next = new URLSearchParams(params); next.delete("showAccess");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 font-sans overflow-x-hidden">
      {/* ===== Top bar ===== */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#090D16]/85 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/evos-logo.png"
              alt="EVOS"
              data-testid="landing-logo"
              className="h-10 w-10 rounded-sm object-cover shadow-[0_0_24px_-4px_rgba(16,185,129,0.45)]"
            />
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">EVOS</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-mono mt-0.5">Cockpit de Operação</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="header-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-slate-700 hover:border-[#3B82F6] text-slate-200 text-xs font-semibold transition-colors"
            >
              <Calendar className="w-3.5 h-3.5" /> Demo Executiva
            </button>
            <button
              data-testid="header-cta-btn"
              onClick={() => setAccessOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#34D399] text-slate-900 text-xs font-bold transition-colors shadow-[0_0_24px_-6px_rgba(16,185,129,0.6)]"
            >
              Acessar Cockpit <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative pt-16 pb-24 lg:pt-28 lg:pb-32 overflow-hidden" data-testid="landing-hero">
        <div className="absolute -top-20 -left-32 w-[420px] h-[420px] rounded-full bg-[#10B981]/12 blur-[120px] pointer-events-none" />
        <div className="absolute top-20 right-0 w-[520px] h-[520px] rounded-full bg-[#3B82F6]/12 blur-[140px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#10B981]/40 bg-[#10B981]/10 backdrop-blur-sm rounded-full mb-8">
            <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full evos-blink" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#10B981]">Para Gestores de TI · CIOs · Diretores de Operações</span>
          </div>

          <h1 className="font-display font-black tracking-[-0.025em] text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-5xl">
            O Cockpit Definitivo para o{" "}
            <span className="bg-gradient-to-r from-[#10B981] via-[#34D399] to-[#3B82F6] bg-clip-text text-transparent">
              Gestor de TI
            </span>{" "}
            de Redes Distribuídas
          </h1>

          <p className="mt-7 text-base sm:text-lg lg:text-xl text-slate-300/90 max-w-3xl leading-relaxed">
            Unifique o controle de <span className="text-slate-100 font-semibold">conectividade, segurança e disponibilidade</span> de
            todas as suas unidades remotas em uma única tela. Monitore múltiplos provedores de internet, gerencie firewalls de forma unificada
            e conte com <span className="text-[#10B981] font-semibold">agentes de IA para resolver incidentes em segundos.</span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-cta-cockpit"
              onClick={() => setAccessOpen(true)}
              className="group inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#10B981] hover:bg-[#34D399] text-slate-900 font-bold text-sm uppercase tracking-wider shadow-[0_0_36px_-8px_rgba(16,185,129,0.7)] hover:shadow-[0_0_50px_-6px_rgba(16,185,129,0.9)] transition-all"
            >
              <Sparkles className="w-4 h-4" /> Experimentar Cockpit Interativo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-demo"
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#3B82F6]/70 hover:border-[#3B82F6] hover:bg-[#3B82F6]/8 text-slate-100 font-semibold text-sm uppercase tracking-wider transition-colors"
            >
              <Calendar className="w-4 h-4" /> Agendar Demo Executiva
            </button>
          </div>

          {/* KPI strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-800/50 border border-slate-800 backdrop-blur-sm">
            {[
              { k: "Resposta", v: "< 2s", sub: "incidentes resolvidos pela IA" },
              { k: "Provedores", v: "1 tela", sub: "fibra, rádio e 4G unificados" },
              { k: "Firewalls", v: "Multi-vendor", sub: "Cisco · Fortinet · Aruba · VMware" },
              { k: "Unidades remotas", v: "12 ativas", sub: "Secretaria de Educação · PE" },
            ].map((kpi) => (
              <div key={kpi.k} className="bg-[#0F172A]/80 px-5 py-5">
                <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500">{kpi.k}</div>
                <div className="font-display font-black text-xl text-slate-50 mt-2">{kpi.v}</div>
                <div className="text-[11px] text-slate-500 mt-1">{kpi.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3 Gargalos ===== */}
      <section className="relative py-20 lg:py-28 border-t border-slate-800/60 bg-[#0B111C]" data-testid="landing-pain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-400 mb-3">Os 3 Grandes Gargalos da TI Distribuída</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em]">
              Sua operação ainda depende de planilhas, ligações e madrugadas?
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              Três problemas consomem o tempo da equipe de TI e expõem o gestor a riscos de indisponibilidade, prejuízo e até manchete na imprensa.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Wifi, title: "Descentralização de Provedores",
                body: "Gerenciar dezenas de contratos de internet (fibra, rádio e 4G) e cobrar SLAs de operadoras diferentes — de forma manual, por e-mail e telefone — é um trabalho hercúleo que nunca acaba.",
              },
              {
                icon: ShieldCheck, title: "Silos de Segurança",
                body: "Cada unidade remota tem um firewall ou roteador de marca diferente, sem política de segurança unificada e sem visão centralizada das ameaças que estão atacando sua rede agora.",
              },
              {
                icon: ServerCrash, title: "Demora na Resposta (MTTR)",
                body: "Depender de intervenção humana manual para descobrir que um link caiu ou para bloquear um IP atacante leva horas — gerando indisponibilidade, prejuízo e ligações no fim de semana.",
              },
            ].map((p) => (
              <div key={p.title} className="group relative border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-7 hover:border-red-500/40 transition-colors">
                <div className="absolute -top-px left-7 w-12 h-px bg-red-500/60" />
                <p.icon className="w-7 h-7 text-red-400 mb-5" />
                <h3 className="font-display font-bold text-xl mb-3">{p.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Como o EVOS Resolve ===== */}
      <section className="relative py-20 lg:py-28 border-t border-slate-800/60" data-testid="landing-solution">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-[#10B981]/8 blur-[140px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#10B981] mb-3">Como o EVOS Resolve</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em]">
              Da reação manual à operação autônoma — em três camadas.
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              Tudo o que o gestor de TI precisa para dormir tranquilo, em um único cockpit visual e acionável.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Globe2, title: "Conectividade Unificada", accent: "#3B82F6",
                body: "Visão em tempo real da saúde de todos os links de internet e túneis VPN de todas as suas unidades remotas — independente do provedor, da operadora ou da tecnologia.",
              },
              {
                icon: Cpu, title: "Telemetria e Saúde de Hardware", accent: "#10B981",
                body: "Monitore o consumo de CPU, memória e armazenamento dos dispositivos de borda em cada localidade. Receba alertas antes que o problema vire incidente.",
              },
              {
                icon: Bot, title: "Resposta Automatizada por IA", accent: "#A855F7",
                body: "Agentes inteligentes detectam quedas de link, realizam failover automático e bloqueiam ataques na borda em segundos — entregando relatórios executivos prontos.",
              },
            ].map((s) => (
              <div key={s.title} className="group relative border border-slate-800 bg-slate-900/50 backdrop-blur-md p-7 hover:border-[#10B981]/50 transition-all">
                <div className="absolute -top-px left-7 w-12 h-px" style={{ background: s.accent }} />
                <div
                  className="w-12 h-12 flex items-center justify-center mb-5 border"
                  style={{ borderColor: s.accent + "55", background: s.accent + "12" }}
                >
                  <s.icon className="w-5 h-5" style={{ color: s.accent }} />
                </div>
                <h3 className="font-display font-bold text-xl mb-3">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Estudo de caso ===== */}
      <section className="relative py-20 lg:py-28 border-t border-slate-800/60 bg-[#0B111C]" data-testid="landing-case">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Validação · Em produção</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em]">
              Aplicado em órgão público de alta complexidade.
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              Implantado na <span className="text-slate-100 font-semibold">Secretaria de Educação do Estado de Pernambuco</span>,
              monitorando 12 unidades georreferenciadas (sede em Recife + 11 Gerências Regionais) com links de fibra, rádio e 4G de operadoras distintas.
              Prova que o cockpit escala em ambientes reais, federados e críticos.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-px bg-slate-800/50 border border-slate-800">
              {[
                { k: "Unidades", v: "12" },
                { k: "Municípios", v: "11" },
                { k: "Tecnologias", v: "Fibra · 4G · SD-WAN" },
              ].map((kpi) => (
                <div key={kpi.k} className="bg-[#0F172A]/80 px-5 py-4">
                  <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500">{kpi.k}</div>
                  <div className="font-display font-black text-base text-slate-50 mt-1">{kpi.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {["Recife (Sede)", "Caruaru", "Garanhuns", "Limoeiro", "Toritama", "Serra Talhada", "Araripina", "Arcoverde", "Salgueiro", "Cabrobó", "Belém do São Francisco"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-300 border border-slate-700 bg-slate-900/60 px-2.5 py-1">
                  <MapPin className="w-3 h-3 text-[#3B82F6]" /> {c}
                </span>
              ))}
            </div>
          </div>

          <div className="relative aspect-square max-w-md mx-auto w-full border border-slate-800 bg-gradient-to-br from-[#0F172A] to-[#020617] p-8 overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "radial-gradient(circle at 30% 40%, rgba(16,185,129,0.18), transparent 40%), radial-gradient(circle at 70% 60%, rgba(59,130,246,0.18), transparent 40%)",
            }} />
            <div className="relative">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-2">Topologia · PE</div>
              <div className="font-display font-black text-2xl mb-1">SEDU-PE</div>
              <div className="text-[11px] text-slate-500 font-mono">12 sites · 11 enlaces · OK</div>
              <svg viewBox="0 0 300 260" className="mt-8 w-full">
                <defs>
                  <radialGradient id="hub-glow"><stop offset="0%" stopColor="#10B981" stopOpacity="0.7" /><stop offset="100%" stopColor="#10B981" stopOpacity="0" /></radialGradient>
                </defs>
                <circle cx="240" cy="170" r="20" fill="url(#hub-glow)" />
                <circle cx="240" cy="170" r="6" fill="#10B981" />
                {[[220,160],[210,180],[170,170],[140,150],[110,180],[80,160],[60,140],[40,120],[80,90],[140,100],[180,130]].map(([x,y],i) => (
                  <g key={i}>
                    <line x1="240" y1="170" x2={x} y2={y} stroke="#3B82F6" strokeWidth="0.8" opacity="0.55" />
                    <circle cx={x} cy={y} r="3" fill="#3B82F6" />
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Closing banner (single CTA — no inline form) ===== */}
      <section className="relative py-20 lg:py-28 border-t border-slate-800/60 overflow-hidden" data-testid="landing-banner">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/10 via-transparent to-[#3B82F6]/10 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-[#10B981]/10 blur-[140px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#10B981]/40 bg-[#10B981]/10 backdrop-blur-sm rounded-full mb-6">
            <Zap className="w-3 h-3 text-[#10B981]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#10B981]">Acesso imediato · Sem instalação</span>
          </div>

          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
            Pronto para ver o EVOS{" "}
            <span className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] bg-clip-text text-transparent">resolver incidentes em tempo real?</span>
          </h2>

          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Experimente o cockpit completo em modo simulação. Provoque uma queda de link, simule um ataque de força bruta e veja a IA mitigar — em segundos.
          </p>

          <div className="mt-10">
            <button
              data-testid="banner-cta-btn"
              onClick={() => setAccessOpen(true)}
              className="group inline-flex items-center justify-center gap-2 px-8 py-5 bg-[#10B981] hover:bg-[#34D399] text-slate-900 font-bold text-base uppercase tracking-wider shadow-[0_0_50px_-8px_rgba(16,185,129,0.9)] hover:shadow-[0_0_70px_-6px_rgba(16,185,129,1)] transition-all"
            >
              <Sparkles className="w-5 h-5" />
              Experimentar o Simulador Interativo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="mt-4 text-[11px] font-mono text-slate-500">
              Acesso liberado em 30 segundos · Apenas e-mail corporativo
            </div>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-slate-800/60 bg-[#070A11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] font-mono text-slate-500">
          <div className="flex items-center gap-3">
            <img src="/evos-logo.png" alt="EVOS" className="h-6 w-6 rounded-sm object-cover" />
            <span>EVOS Cockpit · Operação de TI para redes distribuídas</span>
          </div>
          <div>© 2026 EVOS Platform · Built for critical infrastructure</div>
        </div>
      </footer>

      {/* ===== Modals ===== */}
      {accessOpen && (
        <AccessModal
          onClose={() => setAccessOpen(false)}
          onSuccess={async () => {
            try { localStorage.setItem("evos_access_unlocked", "1"); } catch (_) { /* noop */ }
            await refresh();
            navigate("/dashboard", { replace: true });
          }}
        />
      )}
      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
    </div>
  );
}

// ---------- Access Modal (single source of truth for lead capture) ----------
function AccessModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ nome: "", email: "", organizacao: "", cargo: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim() || !form.email.trim() || !form.organizacao.trim() || !form.cargo.trim()) {
      setError("Todos os campos são obrigatórios.");
      return;
    }
    if (!isCorporateEmail(form.email)) {
      setError("Use um e-mail corporativo ou governamental válido. E-mails comuns (Gmail, Outlook, Yahoo, etc.) não são aceitos.");
      return;
    }
    track(EVENTS.LEAD_FORM_SUBMIT, { organizacao: form.organizacao });
    setSubmitting(true);
    try {
      const { data } = await api.post("/landing/access", {
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        organizacao: form.organizacao.trim(),
        cargo: form.cargo.trim(),
      });
      track(EVENTS.LEAD_CAPTURE_SUCCESS, {
        email_domain: form.email.split("@")[1]?.toLowerCase(),
        organizacao: form.organizacao,
        is_admin: !!data?.is_admin,
      });
      setUserId(data?.user?.user_id);
      toast.success("Acesso liberado", { description: "Bem-vindo ao EVOS Cockpit." });
      await onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível liberar o acesso. Tente novamente.";
      setError(msg);
      toast.error("Acesso negado", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testId="access-modal">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#10B981] mb-3">Acesso Exclusivo · 30s</div>
      <h3 className="font-display font-black text-2xl sm:text-3xl mb-2 tracking-tight">Acesse o Cockpit de Simulação</h3>
      <p className="text-sm text-slate-400 mb-6 leading-relaxed">
        Insira seus dados para experimentar em tempo real o simulador de failover automático de links e a mitigação de ataques por agentes de IA.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field icon={User} label="Nome Completo" required>
          <input data-testid="access-input-nome" type="text" autoComplete="name"
            value={form.nome} onChange={update("nome")} placeholder="Ex: Maria Silva"
            className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2" />
        </Field>
        <Field icon={Mail} label="E-mail Corporativo ou Governamental" required hint="Gmail / Outlook / Yahoo etc. não são aceitos">
          <input data-testid="access-input-email" type="email" autoComplete="email"
            value={form.email} onChange={update("email")} placeholder="maria.silva@orgao.gov.br"
            className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-mono text-sm py-2" />
        </Field>
        <Field icon={Building2} label="Organização / Órgão Público" required>
          <input data-testid="access-input-org" type="text" autoComplete="organization"
            value={form.organizacao} onChange={update("organizacao")} placeholder="Ex: Secretaria de Educação · PE"
            className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2" />
        </Field>
        <Field icon={Briefcase} label="Cargo / Função" required>
          <input data-testid="access-input-cargo" type="text" autoComplete="organization-title"
            value={form.cargo} onChange={update("cargo")} placeholder="Ex: CIO · Diretor de TI · Analista SOC"
            className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2" />
        </Field>

        {error && (
          <div data-testid="access-form-error" className="flex items-start gap-3 border border-red-500/40 bg-red-500/5 text-red-300 text-xs p-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" />
            <span>{error}</span>
          </div>
        )}

        <button
          data-testid="access-submit-btn"
          type="submit"
          disabled={submitting}
          className="w-full bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold text-sm uppercase tracking-wider py-4 flex items-center justify-center gap-2 shadow-[0_0_36px_-8px_rgba(16,185,129,0.7)] transition-all"
        >
          {submitting ? "Liberando acesso..." : "Destravar Acesso"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>

        <p className="text-[10px] text-slate-500 text-center font-mono">
          Ao destravar, você concorda com a captura mínima de dados para fins de auditoria de acesso (LGPD Art. 7º, § 4º).
        </p>
      </form>
    </ModalShell>
  );
}

// ---------- Demo Modal (commercial lead) ----------
function DemoModal({ onClose }) {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", organizacao: "", mensagem: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim() || !form.email.trim() || !form.telefone.trim() || !form.organizacao.trim()) {
      setError("Preencha nome, e-mail, telefone e organização.");
      return;
    }
    if (!isCorporateEmail(form.email)) {
      setError("Use um e-mail corporativo ou governamental válido.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/landing/demo", {
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        telefone: form.telefone.trim(),
        organizacao: form.organizacao.trim(),
        mensagem: form.mensagem.trim(),
      });
      track("schedule_demo_request", { organizacao: form.organizacao });
      toast.success("Solicitação recebida!", { description: "Nosso time comercial entrará em contato em breve." });
      setSent(true);
      setTimeout(onClose, 2200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar. Tente novamente.";
      setError(msg);
      toast.error("Erro", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testId="demo-modal">
      {sent ? (
        <div className="py-12 text-center">
          <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto mb-4" />
          <div className="font-display font-bold text-2xl">Solicitação recebida!</div>
          <div className="text-sm text-slate-400 mt-2">Nosso time comercial entrará em contato em breve.</div>
        </div>
      ) : (
        <>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Sessão Executiva · 30min</div>
          <h3 className="font-display font-black text-2xl sm:text-3xl mb-2 tracking-tight">Agendar Demo Executiva</h3>
          <p className="text-sm text-slate-400 mb-6 leading-relaxed">
            Apresentamos o EVOS aplicado ao seu cenário, com os engenheiros de plataforma. Sem compromisso.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <Field icon={User} label="Nome" required>
              <input data-testid="demo-input-nome" value={form.nome} onChange={update("nome")} placeholder="Seu nome completo"
                className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2" />
            </Field>
            <Field icon={Mail} label="E-mail Corporativo" required>
              <input data-testid="demo-input-email" type="email" value={form.email} onChange={update("email")} placeholder="seu.nome@empresa.com.br"
                className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-mono text-sm py-2" />
            </Field>
            <Field icon={Phone} label="Telefone" required>
              <input data-testid="demo-input-tel" value={form.telefone} onChange={update("telefone")} placeholder="(81) 99999-9999"
                className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-mono text-sm py-2" />
            </Field>
            <Field icon={Building2} label="Organização" required>
              <input data-testid="demo-input-org" value={form.organizacao} onChange={update("organizacao")} placeholder="Nome da empresa ou órgão"
                className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2" />
            </Field>
            <Field icon={Activity} label="Mensagem (opcional)">
              <textarea data-testid="demo-input-msg" rows={3} value={form.mensagem} onChange={update("mensagem")} placeholder="Quantas unidades remotas? Que cenário gostaria de ver?"
                className="w-full bg-transparent border-0 outline-none text-slate-100 placeholder:text-slate-600 font-sans text-sm py-2 resize-none" />
            </Field>

            {error && (
              <div data-testid="demo-form-error" className="flex items-start gap-3 border border-red-500/40 bg-red-500/5 text-red-300 text-xs p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" /><span>{error}</span>
              </div>
            )}

            <button
              data-testid="demo-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#3B82F6] hover:bg-[#60A5FA] disabled:opacity-50 text-white font-bold text-sm uppercase tracking-wider py-4 flex items-center justify-center gap-2 transition-colors"
            >
              <Calendar className="w-4 h-4" /> {submitting ? "Enviando..." : "Solicitar Agendamento"}
            </button>
          </form>
        </>
      )}
    </ModalShell>
  );
}

// ---------- Shared bits ----------
function ModalShell({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 bg-[#020617]/85 backdrop-blur-sm overflow-y-auto" data-testid={testId}>
      <div className="relative w-full max-w-lg border border-slate-700 bg-[#0F172A] p-8 my-8 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200 transition-colors" data-testid={`${testId}-close`}>
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, required, hint, children }) {
  return (
    <label className="block group">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-1.5">
        <Icon className="w-3.5 h-3.5 text-[#10B981]" />
        <span>{label}{required && <span className="text-red-400 ml-1">*</span>}</span>
      </div>
      <div className="border border-slate-700 group-focus-within:border-[#10B981] bg-slate-950/60 px-3 transition-colors">
        {children}
      </div>
      {hint && <div className="text-[10px] text-slate-500 mt-1 font-mono">{hint}</div>}
    </label>
  );
}
